"use client";

import * as React from "react";
import { BREAKPOINT_TABLET } from "@/constants";
import { SCRIPT_URLS } from "@/configs/config";
import {
  hotelDetailPatchPattern,
  hotelOverviewPatchPattern,
} from "@/lib/utils";

/**
 * TriplaBot Component
 *
 * Integrates the Tripla chatbot widget dynamically based on the page route.
 * Loads script only after user interaction for optimal performance.
 * Handles DOM mutations to adjust chat widget styles and positioning,
 * especially for mobile responsiveness.
 */
export default function TriplaBot({
  triplaBookingCode,
}: Readonly<{
  triplaBookingCode?: string;
}>) {
  const [scriptLoaded, setScriptLoaded] = React.useState(false);
  const isLoadingRef = React.useRef(false);

  React.useEffect(() => {
    if (scriptLoaded || isLoadingRef.current) return;
    isLoadingRef.current = true;

    const pathname = window.location.pathname;
    const normalizedPathname = pathname.startsWith("/")
      ? pathname.slice(1)
      : pathname;

    let isHotelPage = false;
    if (hotelOverviewPatchPattern.test(normalizedPathname)) {
      isHotelPage = true;
    } else {
      for (const pattern of Object.values(hotelDetailPatchPattern)) {
        if (pattern.test(normalizedPathname)) {
          isHotelPage = true;
          break;
        }
      }
    }

    const isGotoPassPage = normalizedPathname === "gotopass";

    const bookingCode =
      isHotelPage || isGotoPassPage
        ? triplaBookingCode
        : "64278996c02ea21f3035686d1db01014";

    if (!bookingCode && (isHotelPage || isGotoPassPage)) {
      isLoadingRef.current = false;
      return;
    }

    const script = document.createElement("script");
    script.src = SCRIPT_URLS.tripla;
    script.async = true;
    if (bookingCode) {
      script.setAttribute("data-triplabot-code", bookingCode);
    }
    document.body.appendChild(script);

    setScriptLoaded(true);
  }, [scriptLoaded, triplaBookingCode]);

  /**
   * Handle Tripla widget DOM styling and positioning.
   * Observes and maintains custom styles for mobile responsiveness.
   */
  React.useEffect(() => {
    if (!scriptLoaded) return;

    const bodyObserver = new MutationObserver(() => {
      const triplaAppDOM = document.getElementById("tripla-app");
      const triplaMessageDOM = document.getElementById("tripla-icon-message");
      const triplaMessageBubble = document.getElementById(
        "tripla-chat-circle-icon",
      );
      const triplaMessageWindow = document.getElementById("tripla-chat-window");

      if (triplaAppDOM && !triplaAppDOM.dataset.observed) {
        triplaAppDOM.style.zIndex = "40";
        triplaAppDOM.style.cursor = "pointer";
        triplaAppDOM.dataset.observed = "true";

        /** Observe for any style overrides and reapply */
        const triplaAppObserver = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            if (
              mutation.type === "attributes" &&
              mutation.attributeName === "style"
            ) {
              triplaAppDOM.style.zIndex = "40";
              triplaAppDOM.style.cursor = "pointer";
            }
          }
        });

        triplaAppObserver.observe(triplaAppDOM, {
          attributes: true,
          attributeFilter: ["style"],
        });
      }

      /** Handle Tripla chat message icon (bubble above the bot) */
      if (triplaMessageDOM && !triplaMessageDOM.dataset.observed) {
        if (window.innerWidth < BREAKPOINT_TABLET) {
          triplaMessageDOM.style.bottom = "145px";
        }

        triplaMessageDOM.dataset.observed = "true";
        triplaMessageDOM.style.cursor = "pointer";

        const triplaMessageObserver = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            if (
              mutation.type === "attributes" &&
              mutation.attributeName === "style"
            ) {
              if (window.innerWidth < BREAKPOINT_TABLET) {
                triplaMessageDOM.style.bottom = "145px";
              }
              triplaMessageDOM.style.cursor = "pointer";
            }
          }
        });

        /** Keep mobile positioning fixed on style mutations */
        triplaMessageObserver.observe(triplaMessageDOM, {
          attributes: true,
          attributeFilter: ["style"],
        });
      }

      /** Handle chat circle icon positioning for mobile */
      if (triplaMessageBubble && !triplaMessageBubble.dataset.observed) {
        if (window.innerWidth < BREAKPOINT_TABLET) {
          triplaMessageBubble.style.bottom = "60px";
        }

        triplaMessageBubble.dataset.observed = "true";
        triplaMessageBubble.style.cursor = "pointer";

        const triplaMessageObserver = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            if (
              mutation.type === "attributes" &&
              mutation.attributeName === "style"
            ) {
              if (window.innerWidth < BREAKPOINT_TABLET) {
                triplaMessageBubble.style.bottom = "60px";
              }
              triplaMessageBubble.style.cursor = "pointer";
            }
          }
        });

        triplaMessageObserver.observe(triplaMessageBubble, {
          attributes: true,
          attributeFilter: ["style"],
        });
      }

      /** Handle main Tripla chat window repositioning for smaller screens */
      if (triplaMessageWindow && !triplaMessageWindow.dataset.observed) {
        if (window.innerWidth < BREAKPOINT_TABLET) {
          triplaMessageWindow.style.bottom = "50px";
        }

        triplaMessageWindow.dataset.observed = "true";

        const triplaMessageObserver = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            if (
              mutation.type === "attributes" &&
              mutation.attributeName === "style"
            ) {
              if (window.innerWidth < BREAKPOINT_TABLET) {
                triplaMessageWindow.style.bottom = "50px";
              }
            }
          }
        });

        triplaMessageObserver.observe(triplaMessageWindow, {
          attributes: true,
          attributeFilter: ["style"],
        });
      }
    });

    bodyObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      bodyObserver.disconnect();
    };
  }, [scriptLoaded]);

  return null;
}
